/* eslint max-lines: ["error", {max: 440}] -- Due to long file */
enum AnalyticsFieldName {
  AppUser = "App User",
  AppUserTreatmentPhase = "App User Treatment Phase",
  AppUserUniversalStatusCode = "App User Universal Status Code",
  Component = "Component",
  EntryDate = "Entry Date",
  EventId = "Event Id",
  EventTitle = "Event Title",
  Field = "Field",
  FoodCommentProvided = "Food Comment Provided",
  FoodDescriptionProvided = "Food Description Provided",
  GroupClassId = "groupClassExternalId",
  GroupSectionId = "groupSectionId",
  HasPhoto = "Has Photo",
  InactivityDurationMs = "Inactivity Duration (ms)",
  InactivityLimitMs = "inactivityLimitMs",
  IsAuthenticated = "Is user authenticated",
  IsEditingSeries = "Is editing series",
  IsFirstLaunchSetupDone = "Is first app launch setup done",
  IsInactivityTimeout = "Is inactivity timeout",
  IsNewFood = "Is New Food",
  IsRecommended = "Is recommended",
  IsRecurringEvent = "Is recurring event",
  // true when the action was taken from the "Edit or cancel registration" sheet, i.e. the
  // user switched to a different section of a group they were already registered for
  IsSwitchingSection = "Is switching section",
  LinkType = "Link Type",
  MealType = "Meal Type",
  NewValue = "New value",
  NotificationType = "Notification type",
  PatientId = "patientExternalId",
  Platform = "Platform",
  Reason = "Reason",
  SelectedTimeSlot = "Selected Time Slot",
  SelectedUser = "Selected User",
  Source = "Source",
  Success = "Success",
  Text = "Text",
  TimeSlotWasDefault = "Time Slot Was Default",
  Title = "Title",
  URL = "URL",
  UserRole = "User role",
}

enum AnalyticsComponent {
  Accordion = "Accordion",
  Button = "Button",
  Card = "Card",
  Dropdown = "Dropdown",
  FloatingButton = "Floating Button",
  Tab = "Tab",
}

enum PageViewAnalytics {
  PageViewEventType = "Page View",
  PageViewPageNameProp = "Page Name",
}

const GroupClassSeries = "Group Class Series";

// the surface a group class action was taken from, reported as AnalyticsFieldName.Source.
// Values are spelled out rather than interpolated so the enum doubles as the type
enum GroupClassSeriesAnalyticsSources {
  Card = "Group Class Series - Card",
  DeepLink = "Group Class Series - Deep Link",
  Details = "Group Class Series - Details",
  ExploreTabGroups = "Group Class Series - Explore Tab Groups",
  GroupClassSeriesCarousel = "Group Class Series - Carousel",
  PreviewCard = "Group Class Series Preview - Card",
  RecommendedGroupsList = "Group Class Series Recommended List",
  ScheduleEvent = "Group Class Series - Schedule Event",
  SearchList = "Group Class Series - Search List",
  YourGroups = "Group Class Series - Your Groups",
}

const RecommendedGroupClassModalAnalyticsSource =
  "Recommended Group Class Modal";

const GroupClassSeriesAnalyticsPageView = {
  Details: `${GroupClassSeries} Details`,
  GroupDetailsV2: `${GroupClassSeries} Group Details`,
  List: `${GroupClassSeries} List`,
} as const;

const RecommendedGroupClassModalAnalyticsPageView = {
  Modal: "Recommended Group Class Modal",
} as const;

const GroupClassSeriesAnalyticsEvent = {
  CardPressed: `${GroupClassSeriesAnalyticsSources.Card} pressed`,
  PreviewCardPressed: `${GroupClassSeriesAnalyticsSources.PreviewCard} pressed`,
  RegisterPressed: "Register pressed",
  SearchGroupsPressed: "Search Groups - pressed",
  UnregisterPressed: "Unregister pressed",
  ViewAllGroupsPressed: "View all Groups - pressed",
  YourGroupsEmptyCardSearchGroupsPressed:
    "YourGroups Empty Card - Search Groups - pressed",
} as const;

const GroupSectionAnalyticsEvent = {
  ConfirmRegisterPressed: "Group Section - confirm register - pressed",
  ConfirmUnregisterPressed: "Group Section - confirm unregister - pressed",
  EditRegistrationPressed: "Group Section - edit - pressed",
  RegisterFailed: "Group Section - register - failed",
  RegisterSucceeded: "Group Section - register - succeeded",
  SessionSelected: "Group Section - selected",
  UnregisterFailed: "Group Section - unregister - failed",
  UnregisterSucceeded: "Group Section - unregister - succeeded",
} as const;

const RecommendedGroupClassModalAnalyticsEvent = {
  DeclineReasonSubmitted: "Decline recommended group class reason submitted",
  NoneOfTheseTimesPressed: "None of these times work for me - pressed",
  PickATimePressed: "Pick a time - pressed",
  RemindMeLaterPressed: "Remind me later - pressed",
} as const;

const ProfileAnalyticsPageView = {
  EditNotificationSettings: "Profile Edit Notification Settings Modal",
  EditProfile: "Profile Edit Modal",
  SafetyPlan: "Safety Plan Modal",
} as const;
const Profile = "profile";

const NotificationSettings = "notification settings";
enum ProfileNotificationSettings {
  Email = "email",
  Push = "push",
  Sms = "sms",
}

// eslint-disable-next-line no-restricted-syntax -- this object is used as a collection of constants rather than as an object directly
const ProfileAnalyticsEvent = {
  CalendarPreferenceChanged: "Calendar preference changed",
  CancelEditNotificationSettings: `Cancel edit ${NotificationSettings} - pressed`,
  CancelEditProfile: `Cancel edit ${Profile} - pressed`,
  ContactSupport: "Contact support - pressed",
  DismissEditNotificationSettings: `Dismiss edit ${NotificationSettings} - pressed`,
  DismissEditProfile: `Dismiss edit ${Profile} - pressed`,
  DismissSafetyPlan: "Dismiss safety plan- pressed",
  EditNotificationSettings: `Edit ${NotificationSettings} - pressed`,
  EditProfile: `Edit ${Profile} - pressed`,
  EditProfileFieldPressed: (field: string) =>
    `Edit ${Profile} - ${field} field - pressed`,
  Logout: "Log out - pressed",
  ManageAppPermissions: "Manage app permissions - pressed",
  NotificationSettingChanged: `${NotificationSettings} checkbox pressed`,
  SafetyPlanLinkPressed: "Safety plan - link - pressed",
  SafetyPlanPressed: "Safety plan - pressed",
  SaveChangesEditNotificationSettings: `Save changes edit ${NotificationSettings} - pressed`,
  SaveChangesEditProfile: `Save changes edit ${Profile} - pressed`,
  ShareSafetyPlanPressed: "Share safety plan - pressed",
} as const;

const Home = "Home";
const Onboarding = "Onboarding";
const HomeAnalyticsEvent = {
  HomeTabViewed: `${Home} tab`,
  ResourceCardDismissed: `${Home} Resource Card permanently dismissed`,
  ResourceCardPressed: `${Home} Resource Card pressed`,
  TaskCardPressed: `${Home} Task Card pressed`,
  TaskOpenedFromDeepLink: `${Home} Task opened from deep link`,
  UpcomingAppointmentCardPressed: "Upcoming Appointment Card pressed",
  UpcomingEventCardPressed: "Upcoming Event Card pressed",
  WebRedesignOverviewDismissed: `${Home} Web Redesign Overview dismissed`,
} as const;

const WeightTracking = "Weight Tracking";
const WeightTrackingAnalyticsSources = {
  Section: `${WeightTracking} Section`,
} as const;
const WeightTrackingAnalyticsPageView = {
  History: `${WeightTracking} History`,
} as const;
enum WeightTrackingAnalyticsEvent {
  DismissWeightHistoryPressed = "Dismiss weight history - pressed",
  HideWeightPressed = "Hide weight - pressed",
  ShowWeightPressed = "Show weight - pressed",
  ViewWeightHistoryPressed = "View weight history - pressed",
}

enum TasksAnalyticsEvent {
  ResumeSurvey = "Resume survey - pressed",
  StartSurvey = "Start survey - pressed",
}
enum TasksAnalyticsSources {
  Section = "Home Todo-list Section",
}

enum BookingAnalyticsEvent {
  AddOptionalAttendees = "Add someone else pressed",
  AttendeeSelection = "Attendee selection pressed",
  AvailabilitySelection = "Availability selection pressed",
  Back = "Back pressed",
  Close = "X pressed",
  ConfirmBooking = "Book appointment pressed",
  Continue = "Continue pressed",
  DurationSelection = "Time selection pressed",
  LearnMore = "Learn more CTA pressed",
  LeaveBookingModal = "Leave booking CTA pressed",
  PatientSelection = "Patient selection pressed",
  ProviderSelection = "Provider selection pressed",
  RecurrenceSelection = "Recurrence selection pressed",
  RescheduleScopeSelection = "Reschedule scope selection pressed",
  SessionTypeSelection = "Session type selection pressed",
  StartBooking = "Book an appointment CTA pressed",
  ViewSchedule = "View schedule CTA pressed",
}

enum MessagesAnalyticsScreen {
  AboutMessages = "About Messages Modal",
  Channel = "Chat Channel",
  ChannelDetails = "Chat Channel Details Modal",
  ChannelList = "Chat Channel List",
}
enum MessagesAnalyticsEvent {
  AboutMessagesPressed = "About Messages pressed",
  AddAttachmentPressed = "Add attachment pressed",
  AttachmentMessageSent = "Attachment message sent",
  AttachmentRemoved = "Attachment removed",
  AttachmentSelected = "Attachment selected",
  ChannelDetailsIconPressed = "Chat details icon pressed",
  ChannelOpenedFromDeepLink = "Chat Channel Opened from Deep Link",
  FilesTabSelected = "Files tab selected",
  LearnMorePressed = "Learn more pressed",
  ParticipantsTabSelected = "Participants tab selected",
  SendMessagePressed = "Send message pressed",
}

enum ExploreResourcesAnalyticsEvent {
  InformationalBlurbRead = "Informational Blurb Read",
  ResourcesOverviewCardPressed = "Explore resources overview card pressed",
}

enum MessagesAnalyticsKey {
  ChannelName = "Channel Name",
  IsGroupChannel = "Is Group Chat",
}

const OnboardingAnalyticsPageView = {
  CalendarSync: `${Onboarding} Calendar Sync`,
  Consent: `${Onboarding} Consent`,
  Notifications: `${Onboarding} Notifications Set Up`,
  VerifyInformation: `${Onboarding} Verify Information`,
  Welcome: `${Onboarding} Welcome`,
} as const;

enum LegalLinksAnalyticsEvent {
  PrivacyPolicy = "Privacy Policy pressed",
  TermsOfUse = "Terms of Use pressed",
}

enum OnboardingAnalyticsEvent {
  LetsGoPressed = "Let's go pressed",
  NextButtonPressed = "Next button pressed",
  SkipNotificationsConfirmationPressed = "Skip push notifications confirmation pressed",
  VideoPlayerCardPressed = "Video player card pressed",
}

enum OnboardingV2AnalyticsEvent {
  CareTeamActionPressed = "Onboarding care team action pressed",
  ClinicalPictureActionPressed = "Onboarding clinical picture action pressed",
  ClosePressed = "Onboarding close pressed",
  ContinuePressed = "Onboarding Entrypoint continue pressed",
  ExitKeepGoingPressed = "Exit Onboarding keep going pressed",
  ExitSaveAndLeavePressed = "Exit Onboarding save and leave pressed",
  MedicalReviewExplorePressed = "Onboarding medical review explore pressed",
  MedicalReviewGotItPressed = "Onboarding medical review got it pressed",
  MedicalReviewLearnMorePressed = "Onboarding medical review learn more pressed",
  ProfileActionPressed = "Onboarding profile action pressed",
  TreatmentActionPressed = "Onboarding treatment action pressed",
  YourWhyActionPressed = "Onboarding your why action pressed",
}

const OnboardingV2AnalyticsPageView = {
  Modal: `${Onboarding} Modal`,
  Overview: `${Onboarding} Overview`,
} as const;

const DownloadEnforcement = "Download Enforcement";

const DownloadEnforcementAnalyticsEvent = {
  ClosePressed: `${DownloadEnforcement} close pressed`,
  DownloadPressed: `${DownloadEnforcement} download pressed`,
  Viewed: `${DownloadEnforcement} viewed`,
} as const;

const DownloadEnforcementAnalyticsPageView = {
  Modal: `${DownloadEnforcement} Modal`,
} as const;

// distinguishes where the enforcement modal was shown; sent on every event as AnalyticsFieldName.Platform
enum DownloadEnforcementPlatform {
  Desktop = "Desktop",
  WebOnMobile = "Web on Mobile",
}

enum RootAnalyticsEvent {
  Backgrounded = "App backgrounded",
  Foregrounded = "App foregrounded",
  InactivityBiometricAuthSuccess = "Authenticated via biometrics after inactivity timeout",
  InactivityIgnored = "Inactivity timeout ignored (user already logged out/app not ready)",
  InactivityLoggedOut = "Logged out due to inactivity timeout",
  InactivityTimeout = "Inactivity timeout exceeded",
}

enum ErrorBoundaryEvent {
  RestartAppPressed = "Restart app pressed",
}

const SMT = "SMT";
// eslint-disable-next-line no-restricted-syntax -- Collection of constants
const SMTEntryAnalyticsEvent = {
  AddEntryOpenedFromDeepLink: `${SMT} Add Entry Opened from Deep Link`,
  AddPhotoDismissed: `${SMT} Add photo modal dismissed`,
  AddPhotoPressed: `${SMT} Add photo pressed`,
  AddSMTEntryPressed: `${SMT} Add entry pressed`,
  ChooseFromLibraryPressed: `${SMT} Choose from library pressed`,
  ContinueWithoutPhotoPressed: `${SMT} Continue without photo pressed`,
  DismissEntryModalPressed: `${SMT} Entry modal dismissed`,
  FoodTypeSelected: `${SMT} Food type selected`,
  JournalEntryOpenedFromDeepLink: `${SMT} Entry opened from deep link`,
  NewFoodPressed: `${SMT} New Food checkbox pressed`,
  ReplaceImagePressed: `${SMT} Replace image pressed`,
  SaveEntryPressed: `${SMT} Save entry pressed`,
  SelectPatientDismissed: `${SMT} Patient Selector modal dismissed`,
  SelectPatientPressed: `${SMT} Patient Selector modal patient selected`,
  TakePhotoPressed: `${SMT} Take a photo pressed`,
  ViewAllEntriesOpenedFromDeepLink: `${SMT} View All Entries Opened from Deep Link`,
  ViewAllEntriesPressed: `${SMT} View all entries pressed`,
  ViewFullImagePressed: `${SMT} View full image pressed`,
  ViewSingleDayEntriesToggled: `${SMT} View Single Day Entries Accordion toggled`,
  WelcomeModalConfirmPressed: `${SMT} Welcome modal confirm pressed`,
  WelcomeModalDismissed: `${SMT} Welcome modal dismissed`,
} as const;

const SMTAnalyticsSource = {
  AddSMTEntryFlow: `${SMT} Add Entry Flow`,
  ImagePickerModal: `${SMT} Image Picker Modal`,
  SingleDayEntriesAccordion: `${SMT} Single Day Entries Accordion`,
  SMTEntry: `${SMT} Entry`,
  ViewAllSMTDailyEntriesModal: `${SMT} View All SMT Daily Entries Modal`,
  WeekLogCard: `${SMT} Week Log Card`,
} as const;

const SMTAnalyticsPageView = {
  ViewAllSMTDailyEntriesModal: `${SMT} View All SMT Entries Modal`,
  WelcomeModalPageView: `${SMT} Welcome Modal`,
} as const;

const FileSystemEvent = {
  ImageCompressed: "Image Compressed",
};

const ImageCompressionField = {
  CompressedMediaType: "Compressed Media Type",
  CompressedSizeKB: "Compressed Size (KB)",
  CompressionDurationMs: "Compression Duration (ms)",
  CompressionRate: "Compression Rate",
  OriginalMediaType: "Original Media Type",
  OriginalSizeKB: "Original Size (KB)",
  Source: "Source",
} as const;

enum ImageCompressionSource {
  Chat = "Chat",
  Insurance = "Insurance",
  // eslint-disable-next-line @typescript-eslint/no-shadow -- Safe because consumers access it as ImageCompressionSource.SMT
  SMT = "SMT",
}

const TabAnalyticsPageView = {
  Explore: `Explore ${AnalyticsComponent.Tab}`,
  Profile: `Profile ${AnalyticsComponent.Tab}`,
} as const;

export {
  AnalyticsComponent,
  AnalyticsFieldName,
  BookingAnalyticsEvent,
  DownloadEnforcementAnalyticsEvent,
  DownloadEnforcementAnalyticsPageView,
  DownloadEnforcementPlatform,
  ErrorBoundaryEvent,
  ExploreResourcesAnalyticsEvent,
  FileSystemEvent,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsPageView,
  GroupClassSeriesAnalyticsSources,
  GroupSectionAnalyticsEvent,
  Home,
  HomeAnalyticsEvent,
  ImageCompressionField,
  ImageCompressionSource,
  LegalLinksAnalyticsEvent,
  MessagesAnalyticsEvent,
  MessagesAnalyticsKey,
  MessagesAnalyticsScreen,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
  OnboardingV2AnalyticsEvent,
  OnboardingV2AnalyticsPageView,
  PageViewAnalytics,
  ProfileAnalyticsEvent,
  ProfileAnalyticsPageView,
  ProfileNotificationSettings,
  RecommendedGroupClassModalAnalyticsEvent,
  RecommendedGroupClassModalAnalyticsPageView,
  RecommendedGroupClassModalAnalyticsSource,
  RootAnalyticsEvent,
  SMT,
  SMTAnalyticsPageView,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
  TabAnalyticsPageView,
  TasksAnalyticsEvent,
  TasksAnalyticsSources,
  WeightTrackingAnalyticsEvent,
  WeightTrackingAnalyticsPageView,
  WeightTrackingAnalyticsSources,
};
